// Export PDF + Excel al datelor FILTRATE curent.
//
// PDF  → print-to-PDF cu stylesheet de print curat (fără backend, fără canvas,
//         CSP-safe). Butonul declanșează window.print(); print.css din index.css
//         ascunde navigația/filtrele și aranjează secțiunile pe pagină.
// Excel → SheetJS (xlsx, deja dependință), workbook multi-sheet cu datele derivate.

import {
  activeWindow,
  filteredComenzi,
  deriveProfitabilitate,
  deriveTopClienti,
  deriveMix,
  deriveWaste,
  deriveLivrari,
  deriveExportSplit,
  costInWindow,
} from "@/data/derived";
import { getFilters } from "@/data/filters";
import { labelCategorie, labelSegment } from "@/lib/labels";
import { formatLunaKey } from "@/lib/format";

function stampDesc(): string {
  const f = getFilters();
  const w = activeWindow();
  const parts = [
    `Perioadă: ${formatLunaKey(w.from)} – ${formatLunaKey(w.to)}`,
    `Client: ${f.client === "all" ? "toți" : f.client}`,
    `Categorie: ${f.categorie === "all" ? "toate" : labelCategorie(f.categorie)}`,
    `Segment: ${f.segment === "all" ? "export+intern" : labelSegment(f.segment)}`,
    `Mașină: ${f.masina === "all" ? "toate" : f.masina}`,
  ];
  return parts.join(" · ");
}

// ─── PDF (print) ─────────────────────────────────────────────────────────────
export function exportPdf() {
  window.print();
}

// ─── Excel (SheetJS) ─────────────────────────────────────────────────────────
export async function exportExcel() {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();

  const w = activeWindow();
  const now = new Date();

  // Rezumat / filtre
  const rezumat = [
    { Câmp: "Generat la", Valoare: now.toLocaleString("ro-RO") },
    { Câmp: "Perioadă de la", Valoare: formatLunaKey(w.from) },
    { Câmp: "Perioadă până la", Valoare: formatLunaKey(w.to) },
    { Câmp: "Filtre active", Valoare: stampDesc() },
  ];
  addSheet(XLSX, wb, "Rezumat", rezumat);

  // Cost lunar
  addSheet(
    XLSX,
    wb,
    "Cost lunar",
    costInWindow().map((c) => ({
      Lună: formatLunaKey(c.luna_key),
      "Cost total (€)": c.cost_total_eur,
      "Ore lucrate": c.ore_lucrate,
      "Unități (m²)": c.unitati_m2,
      "Cost/oră (€)": c.cost_pe_ora_eur,
      "Cost/unitate (€)": c.cost_pe_unitate_eur,
    })),
  );

  // Profitabilitate clienți
  addSheet(
    XLSX,
    wb,
    "Profit. clienți",
    deriveProfitabilitate("client")
      .sort((a, b) => b.marja_eur - a.marja_eur)
      .map((r) => ({
        Client: r.valoare,
        Comenzi: r.nr_comenzi,
        "Venit (€)": r.venit_eur,
        "Cost (€)": r.cost_eur,
        "Marjă (€)": r.marja_eur,
        "Marjă %": r.marja_pct,
        "Prag preț min %": r.prag_pret_min_pct,
      })),
  );

  // Profitabilitate categorii
  addSheet(
    XLSX,
    wb,
    "Profit. categorii",
    deriveProfitabilitate("categorie_produs")
      .sort((a, b) => b.marja_eur - a.marja_eur)
      .map((r) => ({
        Categorie: labelCategorie(r.valoare),
        Comenzi: r.nr_comenzi,
        "Venit (€)": r.venit_eur,
        "Cost (€)": r.cost_eur,
        "Marjă (€)": r.marja_eur,
        "Marjă %": r.marja_pct,
      })),
  );

  // Top clienți
  addSheet(
    XLSX,
    wb,
    "Top clienți",
    deriveTopClienti().map((c, i) => ({
      "#": i + 1,
      Client: c.client,
      Segment: c.segment,
      Comenzi: c.nr_comenzi,
      "Venit (€)": c.venit_eur,
      "Marjă (€)": c.marja_eur,
      "Marjă %": c.marja_pct,
      "% din venit": c.pondere_venit_pct,
    })),
  );

  // Mix (pivot categorie × lună)
  const mix = deriveMix();
  addSheet(
    XLSX,
    wb,
    "Mix & sezonalitate",
    mix.serie.map((row) => {
      const out: Record<string, string | number> = { Lună: formatLunaKey(String(row.luna)) };
      for (const cat of mix.categorii) out[labelCategorie(cat)] = (row[cat] as number) ?? 0;
      return out;
    }),
  );

  // Pierderi
  addSheet(
    XLSX,
    wb,
    "Pierderi operații",
    deriveWaste().peOperatie.map((o) => ({
      Operație: o.operatie,
      "Intrare (m²)": o.intrare_m2,
      "Pierdere (m²)": o.pierdere_m2,
      "Pierdere %": o.pierdere_pct,
      "Cost pierdere (€)": o.cost_pierdere_eur,
    })),
  );

  // Livrări
  const livrari = deriveLivrari();
  addSheet(
    XLSX,
    wb,
    "Livrări clienți",
    livrari.topIntarziati.map((c) => ({
      Client: c.client,
      Comenzi: c.nr_comenzi,
      "La timp %": c.procent_la_timp,
      "Întârziere medie (zile)": c.intarziere_medie,
    })),
  );

  // Export split
  addSheet(
    XLSX,
    wb,
    "Export vs intern",
    deriveExportSplit().map((r) => ({
      Segment: labelSegment(r.segment),
      Comenzi: r.nr_comenzi,
      "Venit (€)": r.venit_eur,
      "Pondere venit %": r.pondere_venit_pct,
      "Marjă (€)": r.marja_eur,
      "Marjă %": r.marja_pct,
    })),
  );

  // Comenzi filtrate (fact granular)
  addSheet(
    XLSX,
    wb,
    "Comenzi (filtrate)",
    filteredComenzi().map((c) => ({
      ID: c.id,
      Dată: c.data,
      Lună: c.luna_key,
      Client: c.client,
      Categorie: labelCategorie(c.categorie),
      Segment: labelSegment(c.segment),
      Mașină: c.masina,
      "Cantitate (m²)": c.cantitate_m2,
      "Venit (€)": c.venit_eur,
      "Cost (€)": c.cost_eur,
      "Marjă (€)": c.marja_eur,
      "Marjă %": c.marja_pct,
      "Termen promis": c.termen_promis,
      "Termen realizat": c.termen_realizat,
      "Întârziere (zile)": c.intarziere_zile,
    })),
  );

  const fname = `glass-expert-bi_${now.toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, fname);
}

function addSheet(
  XLSX: typeof import("xlsx"),
  wb: import("xlsx").WorkBook,
  name: string,
  rows: Array<Record<string, string | number>>,
) {
  const data = rows.length ? rows : [{ Info: "Fără date pe filtrul curent" }];
  const ws = XLSX.utils.json_to_sheet(data);
  // lățimi coloane auto (aprox.)
  const cols = Object.keys(data[0]).map((k) => {
    const maxLen = Math.max(k.length, ...data.map((r) => String(r[k] ?? "").length));
    return { wch: Math.min(40, Math.max(8, maxLen + 2)) };
  });
  ws["!cols"] = cols;
  // numele de sheet Excel nu pot conține : \ / ? * [ ] și au max 31 caractere
  const safe = name.replace(/[:\\/?*[\]]/g, "-").slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, safe);
}
